import {
  GridColDef,
  gridNumberComparator,
  GridRenderCellParams,
  GridSortCellParams,
} from "@mui/x-data-grid";
import React, { useEffect } from "react";

import { IDate, timeMappingUpdate } from "../../@types/assetTypes/ap";
import InitiateInvoiceApi from "../../API/InvoicesApi";
import ResponseAPI from "../../API/index";
import { checkIfAdmin } from "../../auth/validateUser";
import SectionConatiner from "../../components/SectionConatiner";
import SpinnerComponent from "../../components/Spinner";
import Table from "../../components/Table";
import ToastComponent from "../../components/Toast";
import useConfig, { Data, Row } from "../../hooks/useConfig";
import useStore from "../../store/useStore";

const columns: GridColDef[] = [
  {
    field: "rateType",
    headerName: "Time Type",
    minWidth: 200,
    editable: true,
    flex: 1,
  },
  {
    field: "employmentType",
    headerName: "Classification",
    minWidth: 150,
    editable: true,
    flex: 1,
  },
  {
    field: "frequency",
    headerName: "Unit of Measure",
    minWidth: 150,
    editable: true,
    flex: 1,
  },
  {
    field: "materialCode",
    headerName: "Material",
    minWidth: 150,
    editable: true,
    flex: 1,
  },
  {
    field: "description",
    headerName: "Description",
    minWidth: 150,
    editable: true,
    flex: 1,
  },
  {
    field: "lastUpdatedBy",
    headerName: "Last Updated by",
    minWidth: 275,
    editable: false,
    flex: 1,
    renderCell: ({ value }: GridRenderCellParams) => {
      if (value)
        return (
          <span
            className="w-full block text-ellipsis overflow-hidden"
            title={value}
          >
            {value}
          </span>
        );
      else return <></>;
    },
  },
  {
    field: "lastUpdatedOn",
    headerName: "Last Updated on",
    minWidth: 175,
    editable: false,
    flex: 1,
    valueFormatter: (value: { value: string }) => value?.value || "",
    sortComparator: (
      v1: IDate,
      v2: IDate,
      cellParams1: GridSortCellParams,
      cellParams2: GridSortCellParams,
    ) => gridNumberComparator(v1?.raw, v2?.raw, cellParams1, cellParams2),
  },
];

const TimeMapping: React.FC = () => {
  const { data, handleError, handleResponse } = useConfig("timeMapping");
  const setLoader = useStore((state) => state.setLoader);
  const toastMessage = useStore((state) => state.toastMessage);

  const disableUpdateFields: string[] = [
    "rateType",
    "employmentType",
    "frequency",
  ];

  const duplicateCheckFields: string[] = [
    "rateType",
    "employmentType",
    "frequency",
  ];

  const isAdmin = checkIfAdmin();

  const mandatoryFields: string[] = [
    "rateType",
    "employmentType",
    "frequency",
    "materialCode",
  ];

  const hideFields: string[] = ["lastUpdatedBy", "lastUpdatedOn"];

  const dataToRow = (data: Data[]): Row[] => {
    return data.map((e: Data): Row => {
      const lastUpdatedDate = e.lastUpdatedDate as IDate;

      return {
        id: e.id as string,
        rateType: e.rateType as string,
        employmentType: e.employmentType as string,
        frequency: e.frequency as string,
        materialCode: e.materialCode as string,
        description: e.description as string,
        lastUpdatedBy: e.lastUpdatedBy as string,
        lastUpdatedOn: lastUpdatedDate,
      };
    });
  };

  const getData = (): Promise<void> => {
    setLoader(true);

    const getRequest = InitiateInvoiceApi.importTimeMapping("GET");

    return ResponseAPI.performRequest(getRequest)
      .then(handleResponse("__get__", dataToRow, ""))
      .catch(handleError);
  };

  const updateData = (
    rowId: string | number | null,
    data: Data,
  ): Promise<void> => {
    setLoader(true);

    const _updateState: timeMappingUpdate = {
      rateType: data.rateType as string,
      employmentType: data.employmentType as string,
      frequency: data.frequency as string,
      materialCode: data.materialCode as string,
      description: data.description as string,
    };

    if (rowId) _updateState.id = rowId.toString();

    const updateRequest = InitiateInvoiceApi.importTimeMapping(
      "PUT",
      _updateState,
    );

    return ResponseAPI.performRequest(updateRequest)
      .then(
        handleResponse(
          rowId ? "__update__" : "__add__",
          dataToRow,
          `${
            rowId ? "Updated" : "Added"
          } successfully! The changes saved will be taken into account on the next AP/AR run.`,
        ),
      )
      .catch(handleError);
  };

  const deleteData = (id: string | number): Promise<void> => {
    setLoader(true);

    const deleteRequest = InitiateInvoiceApi.importTimeMapping(
      "DELETE",
      null,
      id ? { timeIds: [Number(id)] } : undefined,
    );

    return ResponseAPI.performRequest(deleteRequest)
      .then(
        handleResponse(
          "__delete__",
          dataToRow,
          "Deleted successfully! The changes saved will be taken into account on the next AP/AR run.",
        ),
      )
      .catch(handleError);
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <SpinnerComponent />
      {toastMessage.message !== "" ? <ToastComponent /> : null}
      <SectionConatiner
        title="Time Mapping"
        className="w-full mx-auto px-10 relative"
      >
        <Table
          suffix="Time Mapping"
          cols={columns}
          rows={data}
          primaryField="rateType"
          mandatoryFields={mandatoryFields}
          hideFields={hideFields}
          disableUpdateFields={disableUpdateFields}
          duplicateCheckFields={duplicateCheckFields}
          isShowActionsColumn={isAdmin}
          onUpdate={updateData}
          onDelete={deleteData}
        />
      </SectionConatiner>
    </>
  );
};

export default TimeMapping;
